import { Dialog, DialogContent } from "@klarify/ui/components/ui/dialog";
import { cn } from "@klarify/ui/lib/utils";
import type { DialogProps } from "@radix-ui/react-dialog";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { Command as CommandPrimitive } from "cmdk";
import type * as React from "react";

const Command = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof CommandPrimitive>) => (
	<CommandPrimitive
		ref={ref}
		className={cn(
			"flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
			className,
		)}
		{...props}
	/>
);
Command.displayName = CommandPrimitive.displayName;

type CommandDialogProps = DialogProps;

const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
	return (
		<Dialog {...props}>
			<DialogContent className="overflow-hidden p-0">
				<Command className="[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group]]:px-2 **:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3">
					{children}
				</Command>
			</DialogContent>
		</Dialog>
	);
};

const CommandInput = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) => (
	<div
		className="flex items-center border-b px-3"
		cmdk-input-wrapper=""
	>
		<MagnifyingGlassIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
		<CommandPrimitive.Input
			ref={ref}
			className={cn(
				"flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
				className,
			)}
			{...props}
		/>
	</div>
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof CommandPrimitive.List>) => (
	<CommandPrimitive.List
		ref={ref}
		className={cn("max-h-50 overflow-y-auto overflow-x-hidden", className)}
		{...props}
	/>
);

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = ({
	ref,
	...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) => (
	<CommandPrimitive.Empty
		ref={ref}
		className="py-6 text-center text-sm"
		{...props}
	/>
);

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) => (
	<CommandPrimitive.Group
		ref={ref}
		className={cn(
			"overflow-hidden p-1 text-foreground **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:text-xs",
			className,
		)}
		{...props}
	/>
);

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) => (
	<CommandPrimitive.Separator
		ref={ref}
		className={cn("-mx-1 h-px bg-border", className)}
		{...props}
	/>
);
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) => (
	<CommandPrimitive.Item
		ref={ref}
		className={cn(
			"relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0",
			className,
		)}
		{...props}
	/>
);

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
	className,
	ref,
	...props
}: React.ComponentProps<"span">) => {
	return (
		<span
			ref={ref}
			className={cn(
				"ml-auto text-muted-foreground text-xs tracking-widest",
				className,
			)}
			{...props}
		/>
	);
};
CommandShortcut.displayName = "CommandShortcut";

export {
	Command,
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
	CommandShortcut,
};
